import { DAYS_OF_WEEK } from '@/utils/months-and-days';
import { ServiceVariant } from '@/types';
import { getSelectedDayNames } from './getSelectedDays';

const RECURRENCE_DAY_ORDER = DAYS_OF_WEEK.reduce<Record<string, number>>(
  (acc, day, index) => {
    acc[day.recurrence_short_name.toUpperCase()] = index;
    return acc;
  },
  {}
);

export const normalizeRecurrenceDays = (days?: string | null) => {
  if (!days) return [];

  return days
    .split(/[,\|]/)
    .map((day) => day.trim().toUpperCase())
    .filter((day) => day in RECURRENCE_DAY_ORDER)
    .filter((day, index, allDays) => allDays.indexOf(day) === index)
    .sort(
      (firstDay, secondDay) =>
        RECURRENCE_DAY_ORDER[firstDay] - RECURRENCE_DAY_ORDER[secondDay]
    );
};

export const formatRecurringDayIdsForApi = (selectedIds: number[]) =>
  getSelectedDayNames(selectedIds)
    .recurrenceNames.map((day) => day.toUpperCase())
    .join(',');

export const recurrenceDaysChanged = (
  currentDays?: string | null,
  nextDays?: string | null
) =>
  normalizeRecurrenceDays(currentDays).join(',') !==
  normalizeRecurrenceDays(nextDays).join(',');

export const getRecurrenceIntervalFromVariant = (
  variant?: Partial<ServiceVariant> | null
): 1 | 2 | undefined => {
  if (!variant) return undefined;

  const recurrence = variant.service_reccurence ?? variant.service_recurrence;
  const every = Number(recurrence?.every);
  if (every === 7) return 1;
  if (every === 14) return 2;

  const recurrenceText = [
    recurrence?.ref,
    recurrence?.name,
    variant.ref,
    variant.name,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (recurrenceText.includes('fortnight')) return 2;
  if (recurrenceText.includes('weekly')) return 1;

  return undefined;
};

export type BookingEffectiveFromPayload = {
  effective_from_date: string;
  effective_from_session_id?: number;
  first_affected_session_id?: number;
};

export const buildBookingEffectiveFromPayload = ({
  effectiveFromDate,
  effectiveFromSessionId,
  includeUiAlias = true,
}: {
  effectiveFromDate?: string;
  effectiveFromSessionId?: number;
  includeUiAlias?: boolean;
}): Partial<BookingEffectiveFromPayload> => {
  if (!effectiveFromDate) return {};

  return {
    effective_from_date: effectiveFromDate,
    ...(effectiveFromSessionId
      ? {
          ...(includeUiAlias
            ? { first_affected_session_id: effectiveFromSessionId }
            : { effective_from_session_id: effectiveFromSessionId }),
        }
      : {}),
  };
};
